import { useCallback, useEffect, useRef, useState } from 'react';
import { buildSilentPayload, checkHumanSignals, createSilentGuard, type SilentGuardResult, type SilentGuardState } from '../lib/silentGuard';
import { fetchAndSolve, type SolvedChallenge } from '../lib/invisibleCaptcha';
import { clientError } from '../lib/errorCodes';

const INTERACTION_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'mousemove', 'focusin', 'input'] as const;
const MIN_AGE_BUFFER_MS = 250;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export function useSilentGuard() {
  const [honeypot, setHoneypotState] = useState('');
  // Refs, not state: the submit handler (e.g. Enter keydown → submit) runs before React re-renders,
  // so state-based interaction flags would still read false.
  const guard = useRef<SilentGuardState>(createSilentGuard());
  const challenge = useRef<Promise<SolvedChallenge> | null>(null);
  const abort = useRef<AbortController | null>(null);

  /** Starts fetching + solving a fresh invisible challenge in the background. */
  const prepare = useCallback(() => {
    abort.current?.abort();
    const controller = new AbortController();
    abort.current = controller;
    const pending = fetchAndSolve(controller.signal);
    pending.catch(() => undefined);
    challenge.current = pending;
    return pending;
  }, []);

  useEffect(() => {
    const mark = () => {
      guard.current.interacted = true;
    };
    const opts = { passive: true, capture: true } as const;
    INTERACTION_EVENTS.forEach((evt) => window.addEventListener(evt, mark, opts));
    prepare();
    return () => {
      INTERACTION_EVENTS.forEach((evt) => window.removeEventListener(evt, mark, opts));
      abort.current?.abort();
    };
  }, [prepare]);

  const setHoneypot = useCallback((value: string) => {
    guard.current.honeypot = value;
    setHoneypotState(value);
  }, []);

  const reset = useCallback(() => {
    guard.current = { ...createSilentGuard(), interacted: guard.current.interacted };
    setHoneypotState('');
  }, []);

  const validate = useCallback(async (): Promise<SilentGuardResult> => {
    const signalError = checkHumanSignals(guard.current);
    if (signalError) return { ok: false, error: signalError };

    let solved = await (challenge.current ?? prepare()).catch(() => null);
    if (!solved || Date.now() > solved.staleAt) {
      solved = await prepare().catch(() => null);
    }
    if (!solved) return { ok: false, error: clientError('SECURITY_CHECK_UNAVAILABLE') };

    const wait = solved.receivedAt + solved.minAgeMs + MIN_AGE_BUFFER_MS - Date.now();
    if (wait > 0) await sleep(wait);

    // Tokens are single-use; line up the next one for a retry or another submission.
    prepare();
    return { ok: true, payload: buildSilentPayload(guard.current, { token: solved.token, nonce: solved.nonce }) };
  }, [prepare]);

  return { state: { honeypot }, setHoneypot, validate, reset };
}
